import { DomainSheet, DomainUpsertLoadingList } from "@/components/domain";
import { useSyncLoading } from "@/context/sheet";
import { useGetAccommodationsWithFulfillments } from "@/hooks/accommodations";
import { useGetAllReservationServicesWithfulfillments } from "@/hooks/reservation-services";
import { CreateFulfillment, Fulfillment } from "@/models";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { EditFulfillment } from "./(fulfillments)/-components/edit";
import { useUpdateFulfillment } from "@/hooks/fulfillments/update.hooks";

export const Route = createFileRoute(
  "/(reservations)/reservations/$id/fulfillments/$fid/edit/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  return (
    <DomainSheet
      noOutlet
      onClose={() => {
        navigate({ to: "/reservations/$id/fulfillments", params: { id } });
      }}
    >
      <Component />
    </DomainSheet>
  );
}

function Component() {
  const { fid, id } = Route.useParams();

  const {
    isLoading: accommodationsFulfillmentsLoading,
    data: accommodationsFulfillments,
  } = useGetAccommodationsWithFulfillments(id);
  const { isLoading: servicesFulfillmentsLoading, data: servicesFulfillments } =
    useGetAllReservationServicesWithfulfillments(id);
  const isLoading = useMemo(
    () => servicesFulfillmentsLoading || accommodationsFulfillmentsLoading,
    [accommodationsFulfillmentsLoading, servicesFulfillmentsLoading],
  );
  const model = useMemo(() => {
    for (const fulfillment of accommodationsFulfillments?.flatMap(
      (a) => a.fulfillments,
    ) ?? []) {
      if (fulfillment && fulfillment?.id === fid) {
        return fulfillment;
      }
    }
    for (const fulfillment of servicesFulfillments?.flatMap(
      (a) => a.fulfillments,
    ) ?? []) {
      if (fulfillment && fulfillment?.id === fid) {
        return fulfillment;
      }
    }
    return undefined;
  }, [accommodationsFulfillments, fid, servicesFulfillments]);
  useSyncLoading(isLoading);

  if (isLoading || !model) return <DomainUpsertLoadingList />;
  return <Edit fulfillment={model} reservationId={id} />;
}

function Edit({
  fulfillment,
  reservationId,
}: {
  fulfillment: Fulfillment;
  reservationId: string;
}) {
  const navigate = useNavigate();
  const { mutateAsync } = useUpdateFulfillment(reservationId, {
    onSuccess: (f) => {
      navigate({
        to: "/reservations/$id/fulfillments",
        params: { id: f.id.toString() },
      });
    },
  });

  const update = async (f: CreateFulfillment) => {
    await mutateAsync({ id: fulfillment.id, ...f });
  };
  return <EditFulfillment value={fulfillment} onUpsert={update} />;
}
